import {Component, computed, DestroyRef, inject, signal, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoPipe} from "@jsverse/transloco";
import {LOREN_ROUTES} from "../../../../shared/constants/loren-routes.constants";
import {LorenHomeAlarmsTableComponent} from "../loren-home-alarms-table/loren-home-alarms-table.component";
import {LorenParameterCardComponent} from "../../../../shared/modules/loren-parameter-card/loren-parameter-card.component";
import {LorenHomeSelectedAssetsComponent} from "../loren-home-selected-assets/loren-home-selected-assets.component";
import {LorenAlarmsService} from "../../../../shared/services/loren-alarms-service/loren-alarms.service";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {AssetService} from "../../../asset/asset.service";
import {combineLatest} from "rxjs";
import {distinctUntilChanged, map, startWith} from "rxjs/operators";
import {AsyncPipe} from "@angular/common";
import {LorenPageContentComponent} from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {Alarm} from "../../../alarm/alarm.service";
import {HomeAssetMap} from "../../../asset-map/home-asset-map";

@Component({
    imports: [
        TranslocoPipe,
        LorenParameterCardComponent,
        LorenHomeAlarmsTableComponent,
        LorenHomeSelectedAssetsComponent,
        AsyncPipe,
        LorenPageContentComponent,
        MatProgressSpinner,
        HomeAssetMap,
    ],
    selector: "loren-home",
    styleUrls: ["./loren-home.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./loren-home.component.html",
})
export class LorenHomeComponent {
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private assetService = inject(AssetService);
    private alarmsService = inject(LorenAlarmsService);

    private readonly destroyRef = inject(DestroyRef);
    @ViewChild("alarmsTable") alarmsTable: LorenHomeAlarmsTableComponent;
    readonly routes = LOREN_ROUTES;

    assets$ = this.assetService.all.pipe(
        takeUntilDestroyed(this.destroyRef),
    );

    searchQuery$ = this.activatedRoute.queryParams.pipe(
        map(x => x.filterBy?.toLowerCase() as string | undefined),
        distinctUntilChanged(),
    );

    filteredAssets$ = combineLatest([this.assets$, this.searchQuery$]).pipe(
        map(([assets, searchQuery]) => {
            if (!searchQuery) {
                return assets;
            }

            return assets.filter(asset =>
                asset.name.toLowerCase().includes(searchQuery) ||
                asset.description?.toLowerCase().includes(searchQuery)
            );
        }),
    )

    private readonly alarms$ = this.alarmsService.getActiveAlarms()
        .pipe(
            map(x => x.data),
            startWith<Alarm[]>([])
        );
    private alarms = toSignal(this.alarms$);
    alarmsFilter = signal<"warning" | "critical">(null);

    criticalAlarms = computed(() => this.alarms()?.filter((n) => n.isCritical).length ?? 0);
    warningAlarms = computed(() => this.alarms()?.filter((n) => !n.isCritical).length ?? 0);

    navigateTo(navigateTo: string) {
        this.router.navigateByUrl(navigateTo);
    }

    filterAlarmsTable(filter: "critical" | "warning") {
        this.alarmsFilter.set(filter);
        this.alarmsTable?.alarmsTable?._elementRef?.nativeElement?.scrollIntoView({
            behavior: "smooth",
        })
    }
}
